import { createSelector } from 'reselect'
import { SortingHatItem, Subrarity } from '../components/LootTable/items'
import {
  SingleRarityHat,
  SortingHat,
  WeightedHat
} from '../components/LootTable/SortingHat'
import { setDerivedPropertiesForItem } from '../store/items'
import {
  GriffonItem, ISearchTag, ListIdentifier, Rarity, RootState, SearchTag,
  SaddlebagFilterOptions, SortColumn, TreasureTheme
} from '../store/types'
import { sortableItemDate } from '../utils'
import { getSelectedBundle, getSelectedBundleItems } from './bundles'
import { getFilters, getSortColumn, getSortDirection } from './lists'

const getDatabaseContents = (state: RootState) => state.contents
const getContents = createSelector([getDatabaseContents], databaseContents =>
  databaseContents?.map(setDerivedPropertiesForItem),
)

const getSelectedItemId = (state: RootState) => state.selectedItemId

const getListId = (_: RootState, listId: ListIdentifier) => listId
export const getListContents = createSelector(
  [getListId, getContents, getSelectedBundleItems],
  (listId, allContents, bundleContents) => {
    if (listId === 'stash') {
      return bundleContents
    } else {
      return allContents
    }
  },
)

export const getAllSubtypes = createSelector([getContents], contents => {
  if (!contents) {
    return []
  }

  const subtypeSet = new Set(
    contents.map(item => item.itemSubtype).filter((s): s is string => !!s),
  )
  return Array.from(subtypeSet).sort()
})

export const getAllTags = createSelector([getContents], contents => {
  if (!contents) {
    return []
  }

  const tagSet = new Set<string>()
  for (const item of contents) {
    if (item.tags) {
      for (const tag of item.tags) {
        tagSet.add(tag)
      }
    }
  }
  return Array.from(tagSet).sort()
})

export const getFilterOptions = createSelector(
  [getListContents],
  (contents): SaddlebagFilterOptions => {
    const emptyState = {
      attunementType: [],
      itemSubtype: {},
      itemType: [],
      isFree: [],
      treasureTheme: [],
      tags: [],
    }
    if (!contents) {
      return emptyState
    }

    const attunementTypeSet = new Set<string>()
    const itemTypeSet = new Set<string>()
    const itemSubtypeMap: {[itemType: string]: Set<string>} = {}
    const treasureThemeSet = new Set<TreasureTheme>()
    const tagSet = new Set<string>()

    for (const item of contents) {
      if (typeof item.attunement === 'string')
        attunementTypeSet.add(item.attunement)

      itemTypeSet.add(item.itemType)

      if (item.itemSubtype) {
        let set = itemSubtypeMap[item.itemType]
        if (!set) {
          set = new Set()
          itemSubtypeMap[item.itemType] = set
        }
        set.add(item.itemSubtype)
      }

      if (item.treasureTheme) {
        for (const theme of item.treasureTheme) {
          treasureThemeSet.add(theme)
        }
      }

      if (item.tags) {
        for (const tag of item.tags) {
          tagSet.add(tag)
        }
      }
    }

    return {
      attunementType: Array.from(attunementTypeSet).sort(),
      itemType: Array.from(itemTypeSet).sort(),
      itemSubtype: Object.entries(itemSubtypeMap).reduce((ret, curr) => {
        ret[curr[0]] = Array.from(curr[1]).sort()
        return ret
      }, {} as SaddlebagFilterOptions['itemSubtype']),
      treasureTheme: Array.from(treasureThemeSet).sort(),
      tags: Array.from(tagSet).sort(),
    }
  },
)

export const getVisibleItemSubtypeOptions = createSelector(
  [getFilterOptions, getFilters],
  (baseOptions, selectedFilters): string[] => {
    const visibleSubtypes: string[] = []

    for (const filter of selectedFilters.itemType) {
      const subtypes = baseOptions.itemSubtype[filter]
      if (!subtypes) continue
      visibleSubtypes.push(...subtypes)
    }

    return visibleSubtypes.sort()
  },
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
const getAppliedSubtypeFilter = createSelector(
  [getFilters, getVisibleItemSubtypeOptions],
  (filters, visibleSubtypes): string[] => {
    return filters.itemSubtype.filter(s => visibleSubtypes.includes(s))
  },
)

export const allItemNames = createSelector([getListContents], contents =>
  contents ? contents.map(i => ({id: i.id, name: i.name})) : [],
)

export const getVisibleItems = createSelector(
  [getListContents, getFilters],
  (contents, filters): GriffonItem[] => {
    if (!contents) return []

    const mustMatchBy: Set<typeof filters['tagSearch'][0]['type']> = new Set(
      filters.tagSearch.map(t => t.type),
    )

    if (mustMatchBy.size === 0) return contents
    //   return contents.filter(itemViewableByPayTier(isAdmin))

    return contents.filter(item => {
      let textMatches = !(
        mustMatchBy.has('text:nameAndMeta') ||
        mustMatchBy.has('text:description') ||
        mustMatchBy.has('text:flavour')
      )
      let attunementMatches = !mustMatchBy.has('requiresAttunement')
      let itemTypeMatches = !mustMatchBy.has('itemType')
      let rarityMatches = !mustMatchBy.has('rarity')
      let isFreeMatches = !mustMatchBy.has('isFree')
      let significanceMatches = !mustMatchBy.has('significance')
      let cardVolumeMatches = !mustMatchBy.has('cardVolume')
      let bookVolumeMatches = !mustMatchBy.has('bookVolume')
      let treasureThemeMatches = !mustMatchBy.has('treasureTheme')
      let tagMatches = !mustMatchBy.has('tag')

      // Collect all selected treasure themes and tags for efficient checking
      const selectedTreasureThemes = new Set<TreasureTheme>()
      const selectedTags = new Set<string>()
      const requiredTags = new Set<string>() // Tags with "+" prefix (AND logic)
      
      // Collect text searches: required (with "+") and optional (without "+")
      const requiredTextSearches: {
        nameAndMeta: string[]
        description: string[]
        flavour: string[]
      } = {
        nameAndMeta: [],
        description: [],
        flavour: [],
      }
      const optionalTextSearches: {
        nameAndMeta: string[]
        description: string[]
        flavour: string[]
      } = {
        nameAndMeta: [],
        description: [],
        flavour: [],
      }
      
      // Helper to collect text searches by type
      const collectTextSearch = (
        type: 'text:nameAndMeta' | 'text:description' | 'text:flavour',
        value: string,
      ) => {
        const isRequired = value.startsWith('+')
        const searchTerm = isRequired ? value.slice(1) : value
        const target = isRequired ? requiredTextSearches : optionalTextSearches
        
        if (type === 'text:nameAndMeta') {
          target.nameAndMeta.push(searchTerm)
        } else if (type === 'text:description') {
          target.description.push(searchTerm)
        } else if (type === 'text:flavour') {
          target.flavour.push(searchTerm)
        }
      }
      
      for (const term of filters.tagSearch) {
        if (term.type === 'treasureTheme') {
          selectedTreasureThemes.add(term.value as TreasureTheme)
        } else if (term.type === 'tag') {
          const tagValue = term.value as string
          if (tagValue.startsWith('+')) {
            // Tag with "+" prefix: required (AND logic)
            requiredTags.add(tagValue.slice(1))
          } else {
            // Regular tag: optional (OR logic)
            selectedTags.add(tagValue)
          }
        } else if (
          term.type === 'text:nameAndMeta' ||
          term.type === 'text:description' ||
          term.type === 'text:flavour'
        ) {
          collectTextSearch(term.type, term.value as string)
        }
      }

      // Check treasure theme (OR logic: item matches if it has any selected theme)
      if (selectedTreasureThemes.size > 0) {
        const itemThemes = item.treasureTheme || []
        if (itemThemes.some(theme => selectedTreasureThemes.has(theme))) {
          treasureThemeMatches = true
        }
      }

      // Check tags
      // - Regular tags: OR logic (item matches if it has any of them)
      // - Required tags (with "+"): AND logic (item must have all of them)
      if (selectedTags.size > 0 || requiredTags.size > 0) {
        const itemTags = item.tags || []
        
        // Check required tags (AND logic: must have all)
        let hasAllRequiredTags = true
        if (requiredTags.size > 0) {
          hasAllRequiredTags = Array.from(requiredTags).every(tag =>
            itemTags.includes(tag),
          )
        }
        
        // Check optional tags (OR logic: must have at least one)
        let hasAnyOptionalTag = true
        if (selectedTags.size > 0) {
          hasAnyOptionalTag = Array.from(selectedTags).some(tag =>
            itemTags.includes(tag),
          )
        }
        
        // Item matches if: (has all required tags) AND (has any optional tag OR no optional tags selected)
        if (hasAllRequiredTags && hasAnyOptionalTag) {
          tagMatches = true
        }
      }

      // Check text searches
      // - Required text searches: AND logic (item must match all)
      // - Optional text searches: OR logic (item must match any)
      
      // Helper to check if item matches name/meta search
      const matchesNameAndMeta = (term: string): boolean => {
        const lowerTerm = term.toLowerCase()
        return (
          item.name.toLowerCase().includes(lowerTerm) ||
          item.itemType.toLowerCase().includes(lowerTerm) ||
          (item.itemSubtype &&
            item.itemSubtype.toLowerCase().includes(lowerTerm)) ||
          (typeof item.attunement === 'string' &&
            item.attunement.toLowerCase().includes(lowerTerm))
        )
      }
      
      // Helper to check if item matches description search
      const matchesDescription = (term: string): boolean =>
        item.description.toLowerCase().includes(term.toLowerCase())
      
      // Helper to check if item matches flavour search
      const matchesFlavour = (term: string): boolean =>
        Boolean(item.flavour?.toLowerCase().includes(term.toLowerCase()))
      
      // Check if there are any text searches
      const hasTextSearches =
        requiredTextSearches.nameAndMeta.length > 0 ||
        requiredTextSearches.description.length > 0 ||
        requiredTextSearches.flavour.length > 0 ||
        optionalTextSearches.nameAndMeta.length > 0 ||
        optionalTextSearches.description.length > 0 ||
        optionalTextSearches.flavour.length > 0
      
      if (hasTextSearches) {
        // Check required text searches (AND logic: must match all)
        const hasAllRequired =
          requiredTextSearches.nameAndMeta.every(matchesNameAndMeta) &&
          requiredTextSearches.description.every(matchesDescription) &&
          requiredTextSearches.flavour.every(matchesFlavour)
        
        // Check optional text searches (OR logic within each category, AND across categories)
        // Each category must match if it has searches, or pass if empty
        let hasAnyOptionalTextSearch = true
        if (optionalTextSearches.nameAndMeta.length > 0) {
          hasAnyOptionalTextSearch =
            hasAnyOptionalTextSearch &&
            optionalTextSearches.nameAndMeta.some(matchesNameAndMeta)
        }
        if (optionalTextSearches.description.length > 0) {
          hasAnyOptionalTextSearch =
            hasAnyOptionalTextSearch &&
            optionalTextSearches.description.some(matchesDescription)
        }
        if (optionalTextSearches.flavour.length > 0) {
          hasAnyOptionalTextSearch =
            hasAnyOptionalTextSearch &&
            optionalTextSearches.flavour.some(matchesFlavour)
        }
        
        // Item matches if: (has all required) AND (has any optional OR no optional selected)
        if (hasAllRequired && hasAnyOptionalTextSearch) {
          textMatches = true
        }
      }

      for (const term of filters.tagSearch) {
        if (term.type === 'requiresAttunement') {
          if (term.value === !!item.attunement) attunementMatches = true
        } else if (term.type === 'name') {
          if (term.value === item.name) textMatches = true
        } else if (term.type === 'itemType') {
          if (term.value === item.itemType) itemTypeMatches = true
        } else if (term.type === 'rarity') {
          if (term.value === '__multiple_rarities__')
            rarityMatches = rarityMatches || item.rarities.length > 1
          else
            rarityMatches = rarityMatches || item.rarities.includes(term.value)
        } else if (term.type === 'isFree') {
          if (term.value === !!item.free) isFreeMatches = true
        } else if (term.type === 'significance') {
          if (term.value === item.significance) significanceMatches = true
        } else if (term.type === 'cardVolume') {
          if (term.value === item.cardVolume) cardVolumeMatches = true
        } else if (term.type === 'bookVolume') {
          if (term.value === item.bookVolume) bookVolumeMatches = true
        }
      }

      return (
        attunementMatches &&
        itemTypeMatches &&
        rarityMatches &&
        isFreeMatches &&
        significanceMatches &&
        textMatches &&
        bookVolumeMatches &&
        cardVolumeMatches &&
        treasureThemeMatches &&
        tagMatches
      )
    })
  },
)

const transformItemWithRarityForSortingHat = (
  item: GriffonItem,
  rarity: Rarity,
): SortingHatItem => ({
  rarity: rarity,
  consumable:
    item.itemType === 'Potion' ||
    item.itemType === 'Scroll' ||
    Boolean(item.itemSubtype?.match(/(ammunition|arrow|bolt)/i)),
  cursed: item.description.includes('**Curse.**'),
  id: item.id,
  name: item.name,
  attunement: item.attunement,
  itemType: item.itemType,
  itemSubtype: item.itemSubtype,
  patreonArtUrl: item.patreonArtUrl,
  patreonCardUrl: item.patreonCardUrl,
  significance: item.significance,
})

/**
 * The single book volume the list is filtered to, or null. The full-workbook
 * export is offered for the unfiltered ledger and for exactly one book filter.
 */
export const singleBookVolumeFilter = (
  tagSearch: readonly SearchTag[],
): number | null => {
  if (tagSearch.length !== 1) return null
  const [tag] = tagSearch
  if (tag.type !== 'bookVolume') return null
  return typeof tag.value === 'number' ? tag.value : null
}

/**
 * Workbook filename for the full multi-table export: the plain ledger-wide
 * name, or a book-scoped variant when a single book filter is active.
 */
export const fullWorkbookFilename = (
  tagSearch: readonly SearchTag[],
): string => {
  const bookVolume = singleBookVolumeFilter(tagSearch)
  return bookVolume === null
    ? 'TheGriffonsSaddlebag_LootTables'
    : `TheGriffonsSaddlebag_Book${bookVolume}_LootTables`
}

export const getSortingHatsForEntireLedger = createSelector(
  [getContents, getFilters, getListId],
  (items, filters, listId): Array<{sortingHat: SortingHat; title: string}> => {
    // Generated for the unfiltered home list, or one filtered to a single book;
    // any other filter combination falls back to a single custom table.
    if (!items || listId !== 'home') return []

    const bookVolume = singleBookVolumeFilter(filters.tagSearch)
    if (filters.tagSearch.length > 0 && bookVolume === null) return []

    const scoped =
      bookVolume === null
        ? items
        : items.filter(i => i.bookVolume === bookVolume)

    const tableDefs: Array<{
      title: string
      rarity: Rarity
      subrarity?: Subrarity
    }> = [
      {title: 'Table A', rarity: 'common'},
      {title: 'Table B', rarity: 'uncommon', subrarity: 'minor'},
      {title: 'Table C', rarity: 'rare', subrarity: 'minor'},
      {title: 'Table D', rarity: 'very rare', subrarity: 'minor'},
      {title: 'Table E', rarity: 'legendary', subrarity: 'minor'},
      {title: 'Table F', rarity: 'uncommon', subrarity: 'major'},
      {title: 'Table G', rarity: 'rare', subrarity: 'major'},
      {title: 'Table H', rarity: 'very rare', subrarity: 'major'},
      {title: 'Table I', rarity: 'legendary', subrarity: 'major'},
    ]

    return tableDefs.map(({rarity, subrarity, title}) => {
      const sortingHatItems = scoped
        .filter(
          i =>
            i.rarities.includes(rarity) &&
            (subrarity === undefined || i.significance === subrarity),
        )
        .map(i => transformItemWithRarityForSortingHat(i, rarity))
      const sortingHat = new SingleRarityHat(sortingHatItems)
      return {title, sortingHat}
    })
  },
)

export const getSortingHatForVisibleItems = createSelector(
  [getVisibleItems, getFilters],
  (items, {tagSearch}) => {
    const filteredRarities = tagSearch
      .filter(
        (t): t is ISearchTag<'rarity', Rarity> =>
          t.type === 'rarity' && t.value !== '__multiple_rarities__',
      )
      .map(t => t.value)

    const HatClass =
      filteredRarities.length === 1 ? SingleRarityHat : WeightedHat

    return new HatClass(
      items.flatMap(i =>
        i.rarities
          .filter(
            r => filteredRarities.length === 0 || filteredRarities.includes(r),
          )
          .map(r => transformItemWithRarityForSortingHat(i, r)),
      ),
    )
  },
)

const RARITIES: Rarity[] = [
  'common',
  'uncommon',
  'rare',
  'very rare',
  'legendary',
  'artifact',
]

type ItemComparator = (a: GriffonItem, b: GriffonItem) => number

const compareOnRarity: ItemComparator = (a, b) => {
  const aRarities = new Set(a.rarities)
  const bRarities = new Set(b.rarities)

  for (const r of RARITIES) {
    const aHas = aRarities.delete(r)
    const bHas = bRarities.delete(r)

    const aRemaining = aRarities.size
    const bRemaining = bRarities.size

    if (aRemaining !== bRemaining) {
      if (aRemaining === 0) return -1
      else if (bRemaining === 0) return 1
    }

    if (aHas !== bHas) {
      if (aHas) return -1
      else return 1
    }
  }
  return 0
}

const compareOnType: ItemComparator = (a, b) => {
  const typeMatch = a.itemType.localeCompare(b.itemType)
  if (!typeMatch) {
    const aHasSubtype = !!a.itemSubtype
    const bHasSubtype = !!b.itemSubtype
    if (aHasSubtype !== bHasSubtype) {
      if (!aHasSubtype) return -1
      else if (!bHasSubtype) return 1
    }

    if (a.itemSubtype && b.itemSubtype) {
      return a.itemSubtype.localeCompare(b.itemSubtype)
    }
  }
  return typeMatch
}

const compareOnAvailability: ItemComparator = (a, b) => {
  return a.free ? (b.free ? 0 : -1) : b.free ? 1 : 0
}

const compareOnSignificance: ItemComparator = (a, b) => {
  return a.significance
    ? b.significance
      ? a.significance.localeCompare(b.significance) * -1
      : 1
    : b.significance
    ? -1
    : 0
}

const compareOnAttunement: ItemComparator = (a, b) => {
  const attunementA = a.attunement
  const attunementB = b.attunement

  if (typeof attunementA === typeof attunementB) {
    if (typeof attunementA === 'string' && typeof attunementB === 'string') {
      return attunementA.localeCompare(attunementB)
    } else if (
      typeof attunementA === 'boolean' &&
      typeof attunementB === 'boolean'
    ) {
      return attunementA ? (attunementB ? 0 : -1) : attunementB ? 1 : 0
    }
  }

  if (attunementA === true) {
    return -1
  } else if (attunementA === false) {
    return 1
  } else {
    return attunementB ? 1 : -1
  }
}

const compareOnDate: ItemComparator = (a, b) => {
  return sortableItemDate(a).localeCompare(sortableItemDate(b))
}

const comparatorForColumn = (column: SortColumn) =>
  ({
    rarity: compareOnRarity,
    type: compareOnType,
    free: compareOnAvailability,
    significance: compareOnSignificance,
    attunement: compareOnAttunement,
    date: compareOnDate,
    name: null,
  }[column])

export const getSortedVisibleItems = createSelector(
  [getSortColumn, getSortDirection, getVisibleItems],
  (sortColumn, sortDirection, items) => {
    const comparator = comparatorForColumn(sortColumn)
    return [...items].sort((a, b) => {
      let result = comparator ? comparator(a, b) : 0
      if (!result) {
        result = a.name.localeCompare(b.name)
        if (comparator) {
          // if using name as secondary sort, always ascending alphabetical
          result = result * sortDirection
        }
      }
      return result * sortDirection
    })
  },
)

export const itemsById = createSelector([getContents], contents => {
  const result: {[id: string]: GriffonItem} = {}
  if (!contents) return result
  for (const item of contents) {
    result[item.id] = item
  }
  return result
})

export const selectedItem = createSelector(
  [getSelectedItemId, itemsById],
  (id, items) => {
    if (!id) return null
    return items[id] || null
  },
)

export const selectedVisibleItemIndex = createSelector(
  [getSelectedItemId, getSortedVisibleItems],
  (id, items) => {
    if (!id || !items) return null
    const index = items.findIndex(item => item.id === id)
    return index === -1 ? null : index
  },
)

export const getSortedVisibleItemsMinusSelectedBundle = createSelector(
  [getSortedVisibleItems, getSelectedBundleItems],
  (contents, bundleItems) => {
    return contents.filter(i => !bundleItems.includes(i))
  },
)

/** Filename for the full-workbook export, named after the book when filtered. */
export const getFullWorkbookFilename = createSelector(
  [getFilters],
  (filters): string => fullWorkbookFilename(filters.tagSearch),
)

export const getLootTableFilename = createSelector(
  [getSelectedBundle, getListId],
  (bundle, listId): string | undefined => {
    if (listId !== 'stash' || bundle === null) return undefined

    return bundle.name.replaceAll(/[^a-zA-Z0-9]/g, '') + '_LootTable'
  },
)
